import React, { useState, useEffect } from "react";
import { DataTable, Modal } from "@/components/ui";
import { fmtDate } from "@/lib/formatters";
import API from "@/lib/API";
import { ViewOrderItemsModal } from "@/components/ViewOrderItemsModal";
import { API_BASE_URL } from "@/services/api";

import toast from "react-hot-toast";
import { FilterSection } from "@/components/ui/FilterSection";
import { estimationService } from "@/services/estimation_service";
import { orderRequestService } from "@/pages/inventory/services/order_request_service";
import { useUsers } from "@/hooks";
import { installationMemberService } from "@/pages/settings/services/installationMemberService";
import { Eye, Truck, X, Activity, Download } from "lucide-react";
import { PdfViewer } from "@/pages/ordering/components/pdfviewer";
import DispatchManagementModal from './dispatch_management_modal';
import { DeliveryChallanPreviewModal } from './DeliveryChallanPreviewModal';
import { useNavigate } from "react-router-dom";

export const OrderRequestsTable = ({ search = "", filters = {}, setFilters }) => {
  const [requests, setRequests] = useState([]);
  const [installationMembers, setInstallationMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedEstimation, setSelectedEstimation] = useState(null);
  const navigate = useNavigate();
  const [isDispatchModalOpen, setIsDispatchModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isInvoicePreviewOpen, setIsInvoicePreviewOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);

  const handleViewPdf = (row) => {
    if (row.estimation_id) {
      setSelectedEstimation(row.estimation_id);
      setIsViewModalOpen(true);
    } else {
       toast.error("Order details not found.");
    }
  };

  const STATUS_OPTIONS = [
    { value: "SENT_TO_STORE", label: "Store Orders" }
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      const [estRes, membersRes] = await Promise.all([
        orderRequestService.getOrderRequests(),
        installationMemberService.getMembers()
      ]);
      
      if (estRes.success && estRes.data) {
        setRequests(estRes.data);
      }
      if (membersRes.success && membersRes.data) {
        setInstallationMembers(membersRes.data);
      }
    } catch (err) {
      console.error("Failed to fetch data:", err);
      toast.error("Failed to fetch order requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpdateDetails = async (id, field, value) => {
    try {
      await orderRequestService.updateOrderRequest(id, field, value);
      toast.success("Updated successfully");
      fetchData();
    } catch (err) {
      console.error("Failed to update:", err);
      toast.error("Failed to update order details");
    }
  };

  const handleReceive = async (id) => {
    try {
      await orderRequestService.receiveInStore(id);
      toast.success("Order marked as received in store");
      fetchData();
      // Update local selected request state to reflect changes instantly in modal
      setSelectedRequest(prev => prev?._id === id ? { ...prev, work_status: 'RECEIVED_IN_STORE' } : prev);
    } catch (err) {
      toast.error("Failed to receive order");
    }
  };

  const handleDispatch = async (id, dispatchData) => {
    try {
      // Resolve the store from the selected request (customer's store or order store)
      const storeId = selectedRequest?.store?._id || selectedRequest?.store ||
                      selectedRequest?.customer_id?.store?._id || selectedRequest?.customer_id?.store || null;
      await orderRequestService.dispatchOrderRequest(id, dispatchData, storeId);
      toast.success("Order dispatched successfully");
      fetchData();
    } catch (err) {
      toast.error("Failed to dispatch order");
    }
  };

  const filteredRequests = React.useMemo(() => {
    let result = [...requests];

    try {
      const currentUser = JSON.parse(localStorage.getItem('company_user') || '{}');
      const storeRef = currentUser.storeRef;
      if (storeRef && storeRef !== 'all') {
        result = result.filter(r => 
          r.store?._id === storeRef || 
          r.store === storeRef || 
          r.customer_id?.store?._id === storeRef || 
          r.customer_id?.store === storeRef
        );
      }
    } catch (e) {
      console.error('Error filtering by store:', e);
    }

    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(r => 
        r.customer_id?.sdId?.toLowerCase().includes(lower) ||
        r.customer_id?.name?.toLowerCase().includes(lower)
      );
    }
    return result;
  }, [requests, search]);

  const columns = [
    {
      header: "Requested By",
      key: "requestedBy",
      render: (r) => {
        const creatorObj = r.estimation_id?.created_by || r.estimation_id?.requested_by || r.customer_id?.createdBy;
        const creatorName = creatorObj?.name || (creatorObj?.firstName ? `${creatorObj.firstName} ${creatorObj.lastName || ''}`.trim() : null);
        const role = creatorObj?.role;
        return (
          <span className="text-[11px] font-medium text-slate-600 truncate max-w-[120px]">
            {role === 'owner' ? "Owner" : (creatorName || "Unknown")}
          </span>
        );
      },
    },
    {
      header: "SD ID",
      key: "sdId",
      render: (r) => (
        <span className="text-[10px] font-bold text-brand-orange whitespace-nowrap">
          {r.customer_id?.sdId || "—"}
        </span>
      ),
    },
    {
      header: "Customer Name",
      key: "customerName",
      render: (r) => (
        <span className="text-brand-dark tracking-tight text-[12px] font-medium leading-tight truncate">
          {r.customer_id?.name || r.customer_id?.customerName || "—"}
        </span>
      ),
    },
    {
      header: "Phone Number",
      key: "phone",
      render: (r) => (
        <span className="text-[12px] font-semibold text-slate-700">
          {r.customer_id?.phone || r.customer_id?.phoneNumber || "—"}
        </span>
      ),
    },
    {
      header: "Store",
      key: "store",
      render: (r) => (
        <span className="text-[11px] font-medium text-slate-600 capitalize">
          {r.store?.name || r.customer_id?.store?.name || "—"}
        </span>
      ),
    },
    {
      header: "Date",
      key: "createdAt",
      render: (r) => (
        <span className="text-[11px] font-medium text-slate-600">
          {fmtDate(r.createdAt)}
        </span>
      ),
    },
    {
      header: "Work Status",
      key: "workStatus",
      render: (r) => {
        const s = r.work_status || "NOT_STARTED";
        const colors = {
          NOT_STARTED: "bg-slate-100 text-slate-600 border-slate-200",
          IN_PROGRESS: "bg-amber-100 text-amber-700 border-amber-200",
          COMPLETED: "bg-emerald-100 text-emerald-700 border-emerald-200",
          RECEIVED_IN_STORE: "bg-blue-100 text-blue-700 border-blue-200"
        };
        
        return (
          <select
            value={s}
            onChange={(e) => handleUpdateDetails(r._id, "work_status", e.target.value)}
            className={`text-[10px] px-2 py-1 rounded-md font-bold uppercase tracking-wider border outline-none ${colors[s] || colors.NOT_STARTED}`}
          >
            <option value="NOT_STARTED">Not Started</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
            <option value="RECEIVED_IN_STORE">Received</option>
          </select>
        );
      },
    },
    {
      header: "Actions",
      key: "actions",
      render: (r) => (
        <div className="flex gap-2 items-center">
          <button 
            onClick={() => handleViewPdf(r)}
            className="p-1 hover:bg-slate-100 rounded text-brand-dark transition-colors"
            title="View PDF"
          >
            <Eye size={14} />
          </button>
          {['COMPLETED', 'RECEIVED_IN_STORE'].includes(r.work_status) ? (
            <button 
              onClick={() => {
                const sale = {
                  _id: r._id,
                  invoiceNo: r.dispatch_details?.invoice_no || r.dispatch_details?.dispatch_no || r.customer_id?.sdId || r._id.substring(0, 6),
                  date: r.updatedAt,
                  customer: r.customer_id?.name || r.customer_id?.customerName || r.customer_id?.sdId || 'Unknown',
                  customerPhone: r.customer_id?.phone || r.customer_id?.phoneNumber || '',
                  billingAddressLine1: r.dispatch_details?.billed_to || '',
                  deliveryAddressLine1: r.dispatch_details?.shipped_to || '',
                  remarks: r.dispatch_details?.notes || '',
                  storeId: r.store?._id || r.store || r.customer_id?.store?._id || r.customer_id?.store || null,
                  items: (r.estimation_id?.items || []).map(item => {
                    const productTitle = item.item_details?.product || item.item_details?.name || item.item_details?.title || item.name || 'Custom Item';
                    const w = item.width || item.item_details?.width;
                    const h = item.height || item.item_details?.height;
                    const displaySize = item.dimensions || item.item_details?.dimensions || (w && h ? `${w} x ${h}` : '');
                    const productDisplay = displaySize ? `${productTitle} (${displaySize})` : productTitle;
                    
                    const imageUrl = item.image_id ? `${API_BASE_URL}/api/estimations/images/${item.image_id}` : 
                      (item.item_details?.image ? 
                        (item.item_details.image.startsWith('http') ? item.item_details.image : 
                        (item.item_details.image.startsWith('/') ? 
                        `${API_BASE_URL}${item.item_details.image}` : 
                        `${API_BASE_URL}/api/inventory/settings/images/${item.item_details.image}`)) : '');

                    const qty = Number(item.qty) || Number(item.quantity) || 1;
                    return {
                      itemName: productDisplay,
                      qty: qty,
                      image: imageUrl,
                      thickness: item.item_details?.thickness || item.thickness || 'N/A',
                      quality: item.item_details?.quality || item.quality || 'Premium',
                      unit: item.unit || item.item_details?.units || item.item_details?.unit || 'Nos'
                    };
                  })
                };
                setSelectedSale(sale);
                setIsInvoicePreviewOpen(true);
              }}
              className="p-1 hover:bg-slate-100 rounded text-green-600 transition-colors"
              title="Download Delivery Challan"
            >
              <Download size={14} />
            </button>
          ) : (
            <button 
              onClick={() => { setSelectedRequest(r); setIsDispatchModalOpen(true); }}
              className="p-1 hover:bg-slate-100 rounded text-brand-orange transition-colors"
              title="Dispatch Order"
            >
              <Truck size={14} />
            </button>
          )}
          <button 
            onClick={() => { if(r.customer_id?._id) navigate(`/workspace/${r.customer_id._id}?tab=track_order`); }}
            className="p-1 hover:bg-slate-100 rounded text-brand-blue transition-colors"
            title="Track Worker Progress"
          >
            <Activity size={14} />
          </button>
        </div>
      ),
    }
  ];

  return (
    <div className="flex flex-col gap-2 h-full">
      <div className="w-full h-full bg-white rounded-xl shadow-sm border border-slate-200/60 overflow-hidden mt-4">
        <DataTable
          columns={columns}
          data={filteredRequests}
          isLoading={loading}
          className="border-none shadow-none rounded-none"
        />
      </div>
      
      <ViewOrderItemsModal 
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        estimation={selectedEstimation}
      />
      
      <DispatchManagementModal 
        isOpen={isDispatchModalOpen}
        onClose={() => { setIsDispatchModalOpen(false); setSelectedRequest(null); }}
        request={selectedRequest}
        onReceive={handleReceive}
        onDispatch={handleDispatch}
      />
      
      <DeliveryChallanPreviewModal
        isOpen={isInvoicePreviewOpen}
        onClose={() => { setIsInvoicePreviewOpen(false); setSelectedSale(null); }}
        sale={selectedSale}
      />
    </div>
  );
};
